import { clsx } from "clsx";
import {
	Camera,
	Eye,
	FileText,
	Lock,
	Plus,
	Send,
	Upload,
	Video,
	X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { PostWeather } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
	ContinuationPicker,
	type ContinuationTarget,
} from "@/features/feed/components/continuation-picker";
import { useCreatePost } from "@/features/feed/hooks/use-create-post";
import {
	ALLOWED_IMAGE_TYPES,
	ALLOWED_MEDIA_TYPES,
	ALLOWED_VIDEO_TYPES,
	compressImage,
	isAllowedMedia,
	isVideoFile,
	MAX_TOTAL_BYTES,
} from "@/features/feed/utils/image";
import { WEATHER_OPTIONS } from "@/features/feed/utils/weather";
import { StageCombobox } from "@/features/stages/components/stage-combobox";
import { useStages } from "@/features/stages/hooks/use-stages.ts";
import { useIsMobile } from "@/hooks/use-mobile";
import { useActiveContext } from "@/store/use-active-context";

const MAX_MEDIA = 8;
const ACCEPTED_MEDIA = ALLOWED_MEDIA_TYPES.join(",");
const ACCEPTED_IMAGE = ALLOWED_IMAGE_TYPES.join(",");
const ACCEPTED_VIDEO = ALLOWED_VIDEO_TYPES.join(",");

type SelectedMedia =
	| { id: string; pending: true }
	| { id: string; pending?: false; file: File; url: string };

let mediaIdSeq = 0;
function nextMediaId() {
	mediaIdSeq += 1;
	return `m${mediaIdSeq}`;
}

function FieldLabel({ children }: { children: React.ReactNode }) {
	return <span className="new-post__field-label">{children}</span>;
}

export function NewPostDialog() {
	const [open, setOpen] = useState(false);
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [media, setMedia] = useState<SelectedMedia[]>([]);
	const [internal, setInternal] = useState(false);
	const [weather, setWeather] = useState<PostWeather | undefined>();
	const [stageId, setStageId] = useState("");
	const [parentPost, setParentPost] = useState<ContinuationTarget>();
	const [error, setError] = useState<string | null>(null);
	const galleryInputRef = useRef<HTMLInputElement>(null);
	const cameraInputRef = useRef<HTMLInputElement>(null);
	const videoInputRef = useRef<HTMLInputElement>(null);
	const generationRef = useRef(0);
	const controllersRef = useRef<Set<AbortController>>(new Set());
	const mediaRef = useRef(media);

	const isMobile = useIsMobile();
	const { mutateAsync, isPending } = useCreatePost();
	const activeObraId = useActiveContext((state) => state.activeObraId);
	const stagesQuery = useStages(activeObraId ?? undefined);
	const stages = stagesQuery.data?.stages ?? [];

	useEffect(() => {
		mediaRef.current = media;
	}, [media]);

	useEffect(
		() => () => {
			for (const item of mediaRef.current) {
				if (!item.pending) URL.revokeObjectURL(item.url);
			}
		},
		[],
	);

	async function addMedia(files: FileList | null) {
		if (!files) return;
		const incoming = Array.from(files).slice(0, MAX_MEDIA);
		if (incoming.length === 0) return;

		const generation = generationRef.current;
		const batchId = nextMediaId();
		const ids = incoming.map((_, index) => `${batchId}-${index}`);
		const idSet = new Set(ids);

		setMedia((current) => {
			const room = Math.max(0, MAX_MEDIA - current.length);
			const reserved = ids
				.slice(0, room)
				.map((id) => ({ id, pending: true as const }));
			return [...current, ...reserved];
		});

		const controller = new AbortController();
		controllersRef.current.add(controller);

		const results = await Promise.all(
			incoming.map((file) =>
				isVideoFile(file)
					? Promise.resolve(file)
					: compressImage(file, controller.signal).catch(() => file),
			),
		);

		controllersRef.current.delete(controller);
		if (generation !== generationRef.current) return;

		const fileById = new Map<string, File>();
		ids.forEach((id, index) => {
			const file = results[index];
			if (file && isAllowedMedia(file)) fileById.set(id, file);
		});

		setMedia((current) =>
			current.flatMap((item) => {
				if (!idSet.has(item.id)) return [item];
				const file = fileById.get(item.id);
				if (!file) return [];
				return [{ id: item.id, file, url: URL.createObjectURL(file) }];
			}),
		);

		if (results.some((file) => !isAllowedMedia(file))) {
			setError(
				"Alguns arquivos foram ignorados (formato não suportado ou acima do limite: 5MB por imagem, 50MB por vídeo).",
			);
		} else {
			setError(null);
		}
	}

	function removeMedia(id: string) {
		setMedia((current) => {
			const target = current.find((item) => item.id === id);
			if (target && !target.pending) URL.revokeObjectURL(target.url);
			return current.filter((item) => item.id !== id);
		});
	}

	function reset() {
		generationRef.current += 1;
		for (const controller of controllersRef.current) controller.abort();
		controllersRef.current.clear();
		setMedia((current) => {
			for (const item of current) {
				if (!item.pending) URL.revokeObjectURL(item.url);
			}
			return [];
		});
		setTitle("");
		setDescription("");
		setInternal(false);
		setWeather(undefined);
		setStageId("");
		setParentPost(undefined);
		setError(null);
	}

	function handleOpenChange(next: boolean) {
		setOpen(next);
		if (!next) reset();
	}

	const readyFiles = media.flatMap((item) => (item.pending ? [] : [item.file]));
	const mediaPending = media.some((item) => item.pending);
	const oversize =
		readyFiles.reduce((sum, file) => sum + file.size, 0) > MAX_TOTAL_BYTES;

	async function submit(status: "draft" | "published") {
		setError(null);
		try {
			await mutateAsync({
				title,
				body: description,
				status,
				internal,
				weatherCondition: weather,
				stageId: stageId || undefined,
				parentPostId: parentPost?.id,
				media: readyFiles,
			});
			handleOpenChange(false);
		} catch {
			setError("Não foi possível salvar o relato. Tente novamente.");
		}
	}

	const canSubmit =
		title.trim() !== "" &&
		description.trim() !== "" &&
		!isPending &&
		!mediaPending &&
		!oversize;

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button>
					<Plus /> Novo relato
				</Button>
			</DialogTrigger>

			<DialogContent className="new-post__dialog">
				<DialogHeader className="new-post__header">
					<DialogTitle>Novo relato</DialogTitle>
					<DialogDescription>
						Registre o que aconteceu hoje na obra
					</DialogDescription>
				</DialogHeader>

				<div className="new-post__body">
					<div className="new-post__field">
						<FieldLabel>Título</FieldLabel>
						<Input
							placeholder="Ex.: Conclusão da impermeabilização da laje L4"
							value={title}
							onChange={(event) => setTitle(event.target.value)}
						/>
					</div>

					<div className="new-post__field">
						<FieldLabel>Descrição</FieldLabel>
						<Textarea
							className="new-post__textarea"
							placeholder="O que foi executado, equipes envolvidas, observações…"
							value={description}
							onChange={(event) => setDescription(event.target.value)}
						/>
					</div>

					<div className="new-post__section">
						<FieldLabel>Clima</FieldLabel>
						<div className="new-post__weather-row">
							<div className="new-post__weather-group">
								{WEATHER_OPTIONS.map((option) => {
									const Icon = option.icon;
									return (
										<button
											key={option.value}
											type="button"
											onClick={() =>
												setWeather((current) =>
													current === option.value ? undefined : option.value,
												)
											}
											className={clsx(
												"new-post__weather-option",
												weather === option.value && "is-active",
											)}
										>
											<Icon
												className="feed-weather-icon"
												data-weather={option.value}
											/>{" "}
											{option.label}
										</button>
									);
								})}
							</div>
						</div>
					</div>

					<div className="new-post__field">
						<FieldLabel>Etapa do cronograma</FieldLabel>
						<StageCombobox
							stages={stages}
							value={stageId}
							onChange={setStageId}
							placeholder="Nenhuma etapa"
							noneLabel="Nenhuma etapa"
							modal
							labelFromValue
						/>
					</div>

					<div className="new-post__field">
						<FieldLabel>Continuar relato</FieldLabel>
						<ContinuationPicker value={parentPost} onChange={setParentPost} />
					</div>

					<div className="new-post__media-field">
						<FieldLabel>Mídia</FieldLabel>
						<input
							ref={galleryInputRef}
							type="file"
							accept={ACCEPTED_MEDIA}
							multiple
							className="new-post__file-input"
							onChange={(event) => {
								addMedia(event.target.files);
								event.target.value = "";
							}}
						/>
						{isMobile && (
							<input
								ref={cameraInputRef}
								type="file"
								accept={ACCEPTED_IMAGE}
								capture="environment"
								className="new-post__file-input"
								onChange={(event) => {
									addMedia(event.target.files);
									event.target.value = "";
								}}
							/>
						)}
						{isMobile && (
							<input
								ref={videoInputRef}
								type="file"
								accept={ACCEPTED_VIDEO}
								capture="environment"
								className="new-post__file-input"
								onChange={(event) => {
									addMedia(event.target.files);
									event.target.value = "";
								}}
							/>
						)}
						<div className="new-post__media-grid">
							{media.map((item) =>
								item.pending ? (
									<div
										key={item.id}
										className="new-post__media-pending"
										aria-busy="true"
									/>
								) : (
									<div key={item.id} className="new-post__media-item">
										{isVideoFile(item.file) ? (
											<video
												src={item.url}
												className="new-post__media-video"
												muted
												playsInline
											/>
										) : (
											<img
												src={item.url}
												alt="Pré-visualização"
												className="new-post__media-img"
											/>
										)}
										<button
											type="button"
											onClick={() => removeMedia(item.id)}
											aria-label="Remover mídia"
											className="new-post__media-remove"
										>
											<X />
										</button>
									</div>
								),
							)}
							{media.length < MAX_MEDIA && (
								<>
									{isMobile && (
										<button
											type="button"
											onClick={() => cameraInputRef.current?.click()}
											className="new-post__media-camera"
										>
											<Camera />
											<span className="new-post__media-add-label">
												Tirar foto
											</span>
										</button>
									)}
									{isMobile && (
										<button
											type="button"
											onClick={() => videoInputRef.current?.click()}
											className="new-post__media-camera"
										>
											<Video />
											<span className="new-post__media-add-label">
												Gravar vídeo
											</span>
										</button>
									)}
									<button
										type="button"
										onClick={() => galleryInputRef.current?.click()}
										className="new-post__media-add"
									>
										<Upload />
										<span className="new-post__media-add-label">Adicionar</span>
									</button>
								</>
							)}
						</div>
						{oversize && (
							<p className="new-post__error">
								Os arquivos ultrapassam o limite de envio. Remova ou substitua
								algum.
							</p>
						)}
					</div>

					<button
						type="button"
						onClick={() => setInternal((value) => !value)}
						className={clsx(
							"new-post__internal-toggle",
							internal && "is-internal",
						)}
					>
						<Checkbox
							checked={!internal}
							className="new-post__internal-checkbox"
						/>
						<div className="new-post__internal-body">
							<div className="new-post__internal-label">
								{internal ? (
									<>
										<Lock className="new-post__lock-icon" />
										Relato interno
									</>
								) : (
									<>
										<Eye className="new-post__visible-icon" />
										Visível para o cliente
									</>
								)}
							</div>
							<div className="new-post__internal-hint">
								{internal
									? "Este relato será visível apenas para a equipe técnica."
									: "O proprietário verá este relato no feed dele."}
							</div>
						</div>
					</button>

					{error && <p className="new-post__error">{error}</p>}
				</div>

				<DialogFooter className="new-post__footer">
					<DialogClose asChild>
						<Button variant="outline" disabled={isPending}>
							Cancelar
						</Button>
					</DialogClose>
					<Button
						variant="outline"
						onClick={() => submit("draft")}
						disabled={!canSubmit}
					>
						<FileText /> Salvar rascunho
					</Button>
					<Button onClick={() => submit("published")} disabled={!canSubmit}>
						<Send /> Publicar
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
